import {
  Component,
  NgZone,
  EventEmitter,
  Output,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleMapsLoaderService } from 'src/app/app-services/google-maps.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'ng-bee-w-h-agm-core-search-util',
  templateUrl: './w-h-agm-core-search-util.component.html',
  standalone: false,
})
export class WHAgmCoreSearchUtilComponent {
  // Emit the raw resolved Place; the consuming component owns the DTO,
  // country-check and form-patching logic so the same rules apply across
  // every location-selection entry point.
  @Output() public placeSelected: EventEmitter<google.maps.places.Place> =
    new EventEmitter();

  private readonly ngZone = inject(NgZone);
  private readonly googleMapsService = inject(GoogleMapsLoaderService);

  public readonly tabIndex = input<number>(0);

  private placesLib: google.maps.PlacesLibrary | null = null;
  private placesLibPromise: Promise<void> | null = null;
  private sessionToken: google.maps.places.AutocompleteSessionToken | null =
    null;
  private readonly searchInput$ = new Subject<string>();
  private currentRequestId = 0;
  public predictions: google.maps.places.AutocompleteSuggestion[] = [];

  constructor() {
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((value) => this.fetchPredictions(value));
  }

  // Loads the Places library once on first use and caches the promise so
  // concurrent callers share the same in-flight load.
  private ensurePlacesLoaded(): Promise<void> {
    if (this.placesLibPromise !== null) {
      return this.placesLibPromise;
    }
    this.placesLibPromise = (async () => {
      await this.googleMapsService.load();
      this.placesLib = (await google.maps.importLibrary(
        'places',
      )) as google.maps.PlacesLibrary;
      this.startNewSession();
    })().catch((error) => {
      console.log('Failed to initialize Places API', error);
      // Allow a retry on the next search after a failure.
      this.placesLibPromise = null;
      throw error;
    });
    return this.placesLibPromise;
  }

  private startNewSession(): void {
    if (!this.placesLib) {
      return;
    }
    this.sessionToken = new this.placesLib.AutocompleteSessionToken();
  }

  public onSearchInput(value: string): void {
    this.searchInput$.next(value);
  }

  private async fetchPredictions(value: string): Promise<void> {
    if (!value || value.trim().length < 3) {
      // Invalidate any in-flight request so a late resolve can't repopulate.
      this.currentRequestId++;
      this.predictions = [];
      return;
    }

    try {
      await this.ensurePlacesLoaded();
    } catch {
      this.predictions = [];
      return;
    }

    if (!this.placesLib) {
      this.predictions = [];
      return;
    }

    if (!this.sessionToken) {
      this.startNewSession();
    }

    const requestId = ++this.currentRequestId;
    const request: google.maps.places.AutocompleteRequest = {
      input: value,
      sessionToken: this.sessionToken,
    };

    try {
      const { suggestions } =
        await this.placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request,
        );

      if (requestId !== this.currentRequestId) {
        return;
      }

      this.ngZone.run(() => {
        const byPlaceId = new Map<
          string,
          google.maps.places.AutocompleteSuggestion
        >();
        suggestions.forEach((suggestion) => {
          const placeId = suggestion.placePrediction?.placeId;
          if (placeId && !byPlaceId.has(placeId)) {
            byPlaceId.set(placeId, suggestion);
          }
        });
        this.predictions = Array.from(byPlaceId.values()).slice(0, 12);
      });
    } catch (error) {
      console.log('Failed to fetch autocomplete suggestions', error);
      if (requestId === this.currentRequestId) {
        this.ngZone.run(() => (this.predictions = []));
      }
    }
  }

  public async onPredictionSelected(
    suggestion: google.maps.places.AutocompleteSuggestion,
  ): Promise<void> {
    const placePrediction = suggestion?.placePrediction;
    if (!placePrediction) {
      return;
    }

    // Invalidate any in-flight autocomplete request so a late resolve after
    // selection can't repopulate the list.
    this.currentRequestId++;

    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: [
          'displayName',
          'formattedAddress',
          'location',
          'addressComponents',
          'types',
        ],
      });

      // Session terminates on fetchFields — a new token must be generated
      // per Google's billing model.
      this.startNewSession();
      this.predictions = [];
      this.placeSelected.emit(place);
    } catch (error) {
      console.log('Failed to resolve place details', error);
    }
  }

  public displayPrediction = (
    suggestion: google.maps.places.AutocompleteSuggestion | null,
  ): string => {
    return suggestion?.placePrediction?.text?.text || '';
  };
}
